import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import {
  Team,
  TeamDetail,
  TeamMember,
  CreateTeamRequest,
  UpdateTeamRequest,
  AddTeamMemberRequest,
  InviteTeamMemberRequest,
  UpdateMemberRoleRequest,
  TeamInvitation,
  UserTeam
} from '../models/team.model';

@Injectable({
  providedIn: 'root'
})
export class TeamService {
  private readonly API_URL = `${environment.apiUrl}/global_admin/teams`;

  constructor(private http: HttpClient) {}

  /**
   * Get all teams accessible to current user
   */
  getTeams(): Observable<Team[]> {
    return this.http.get<Team[]>(`${this.API_URL}`);
  }

  /**
   * Get team by ID with details
   */
  getTeamById(id: string): Observable<TeamDetail> {
    return this.http.get<TeamDetail>(`${this.API_URL}/${id}`);
  }

  /**
   * Create a new team
   */
  createTeam(request: CreateTeamRequest): Observable<Team> {
    return this.http.post<Team>(`${this.API_URL}`, request);
  }

  /**
   * Update team
   */
  updateTeam(id: string, request: UpdateTeamRequest): Observable<Team> {
    return this.http.put<Team>(`${this.API_URL}/${id}`, request);
  }

  /**
   * Delete team
   */
  deleteTeam(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${id}`);
  }

  /**
   * Get team members
   */
  getTeamMembers(teamId: string): Observable<TeamMember[]> {
    return this.http.get<TeamMember[]>(`${this.API_URL}/${teamId}/members`);
  }

  /**
   * Add team member
   */
  addTeamMember(teamId: string, request: AddTeamMemberRequest): Observable<TeamMember> {
    return this.http.post<TeamMember>(`${this.API_URL}/${teamId}/members`, request);
  }

  /**
   * Remove team member
   */
  removeTeamMember(teamId: string, memberId: string): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${teamId}/members/${memberId}`);
  }

  /**
   * Update member role
   */
  updateMemberRole(teamId: string, memberId: string, request: UpdateMemberRoleRequest): Observable<TeamMember> {
    return this.http.patch<TeamMember>(`${this.API_URL}/${teamId}/members/${memberId}/role`, request);
  }

  /**
   * Invite team member
   */
  inviteTeamMember(teamId: string, request: InviteTeamMemberRequest): Observable<{ token: string; message: string }> {
    return this.http.post<{ token: string; message: string }>(`${this.API_URL}/${teamId}/invitations`, request);
  }

  /**
   * Get team directory with search
   */
  getTeamDirectory(teamId: string, search?: string): Observable<TeamMember[]> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<TeamMember[]>(`${this.API_URL}/${teamId}/directory`, { params });
  }

  /**
   * Get user's teams
   */
  getUserTeams(userId: string): Observable<UserTeam[]> {
    return this.http.get<UserTeam[]>(`${environment.apiUrl}/users/${userId}/teams`);
  }

  /**
   * Get user's team invitations
   */
  getUserInvitations(userId: string): Observable<TeamInvitation[]> {
    return this.http.get<TeamInvitation[]>(`${environment.apiUrl}/users/${userId}/invitations`);
  }

  /**
   * Accept team invitation
   */
  acceptInvitation(invitationId: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/invitations/${invitationId}/accept`, {});
  }

  /**
   * Reject team invitation
   */
  rejectInvitation(invitationId: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/invitations/${invitationId}/reject`, {});
  }

  /**
   * Leave team
   */
  leaveTeam(teamId: string): Observable<void> {
    return this.http.post<void>(`${this.API_URL}/${teamId}/leave`, {});
  }

  /**
   * Get team (simplified version for listing)
   */
  getTeam(id: string): Observable<Team> {
    return this.http.get<Team>(`${this.API_URL}/${id}/info`);
  }
}
